import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useSubscription } from "../contexts/SubscriptionProvider";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Cloud, ShieldAlert, TrendingUp, ChevronRight, AlertTriangle, Loader2,
} from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

/**
 * Executive Cloud Security Posture widget for the Dashboard.
 * Only renders when the user's tier includes `evidence_cloud_connectors`
 * (Pro/Enterprise).
 */
const CloudPostureWidget = () => {
  const { token } = useAuth();
  const { locale } = useI18n();
  const { hasFeature } = useSubscription();
  const navigate = useNavigate();
  const isES = locale === "es";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const canView = hasFeature("evidence_cloud_connectors");

  useEffect(() => {
    if (!canView || !token) { setLoading(false); return; }
    axios
      .get(`${API}/cloud-scanners/posture`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token, canView]);

  if (!canView) return null;
  if (loading) return null;

  const empty = !data || data.total_scans === 0 || !data.by_provider?.length;

  const scoreColor = (score) => {
    if (score === null || score === undefined) return "text-muted-foreground";
    if (score >= 85) return "text-emerald-500";
    if (score >= 65) return "text-amber-500";
    return "text-red-500";
  };

  const severityTone = (sev) => ({
    critical: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
    high: "bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30",
    medium: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  })[sev] || "bg-blue-500/10 text-blue-600 border-blue-500/30";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="border-border overflow-hidden" data-testid="cloud-posture-widget">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Cloud className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">
                  {isES ? "Postura de Seguridad en la Nube" : "Cloud Security Posture"}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {isES
                    ? "Panorama ejecutivo de tu exposición en AWS, Azure, GCP, GitHub y Okta"
                    : "Executive view across AWS, Azure, GCP, GitHub and Okta"}
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate("/evidence-collection")}
              data-testid="posture-goto-scanners"
            >
              {isES ? "Ir a escáneres" : "Go to scanners"}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {empty ? (
            <div className="p-8 text-center space-y-3">
              <Cloud className="w-10 h-10 mx-auto text-muted-foreground opacity-40" />
              <p className="text-sm text-muted-foreground">
                {isES
                  ? "Aún no has ejecutado escaneos en la nube. Conecta un proveedor y corre tu primer escaneo para ver tu score."
                  : "No cloud scans yet. Connect a provider and run your first scan to see your score."}
              </p>
              <Button size="sm" onClick={() => navigate("/evidence-collection")}
                data-testid="posture-start-scan">
                {isES ? "Empezar" : "Get started"}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
              {/* Score + Trend */}
              <div className="lg:col-span-1 p-5 border-b lg:border-b-0 lg:border-r border-border flex flex-col items-center justify-center text-center">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                  {isES ? "Score global" : "Global score"}
                </div>
                <div className={`text-6xl font-heading font-bold ${scoreColor(data.score)}`}
                  data-testid="posture-score">
                  {data.score ?? "—"}
                  <span className="text-xl text-muted-foreground">/100</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                  <ShieldAlert className="w-3 h-3" />
                  {data.by_provider.length} {isES ? "proveedores escaneados" : "providers scanned"} ·{" "}
                  {data.total_scans} {isES ? "escaneos" : "scans"}
                </div>

                {data.trend.length > 1 && (
                  <div className="w-full mt-4 h-20">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data.trend}>
                        <defs>
                          <linearGradient id="postureGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.4} />
                            <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" hide />
                        <YAxis domain={[0, 100]} hide />
                        <Tooltip
                          contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: "11px" }}
                        />
                        <Area type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={2}
                          fill="url(#postureGrad)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Per provider breakdown */}
              <div className="lg:col-span-1 p-5 border-b lg:border-b-0 lg:border-r border-border">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-3">
                  <TrendingUp className="w-3 h-3" />
                  {isES ? "Por proveedor" : "Per provider"}
                </div>
                <div className="space-y-2">
                  {data.by_provider.map((p) => (
                    <div
                      key={p.provider}
                      className="flex items-center justify-between p-2 rounded bg-muted/30 hover:bg-muted/50 transition"
                      data-testid={`posture-row-${p.provider}`}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Cloud className="w-3.5 h-3.5 shrink-0 text-primary" />
                        <span className="text-sm font-medium capitalize truncate">
                          {p.provider_name}
                        </span>
                        <Badge variant="outline" className="text-[9px] ml-1 shrink-0">
                          {p.mode?.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="flex gap-1.5 text-[10px]">
                          <span className="text-emerald-600" title={isES ? "Cumple" : "Compliant"}>
                            ✓ {p.counts?.compliant || 0}
                          </span>
                          <span className="text-red-600" title={isES ? "No cumple" : "Non-compliant"}>
                            ✗ {p.counts?.non_compliant || 0}
                          </span>
                          <span className="text-amber-600" title={isES ? "Advertencia" : "Warning"}>
                            ⚠ {p.counts?.warning || 0}
                          </span>
                        </div>
                        <span className={`text-sm font-bold tabular-nums ${scoreColor(p.score)}`}>
                          {p.score}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top critical findings */}
              <div className="lg:col-span-1 p-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-3">
                  <AlertTriangle className="w-3 h-3" />
                  {isES ? "Top pendientes críticos" : "Top critical findings"}
                </div>
                {data.top_findings.length === 0 ? (
                  <div className="text-xs text-muted-foreground py-4 text-center">
                    {isES ? "✓ Sin hallazgos críticos pendientes" : "✓ No critical findings pending"}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {data.top_findings.map((f) => (
                      <div
                        key={f.finding_id}
                        className="p-2 rounded border border-border hover:border-primary/40 cursor-pointer transition"
                        onClick={() => navigate("/evidence-collection")}
                        data-testid={`posture-finding-${f.finding_id}`}
                      >
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-red-500" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-xs font-medium truncate">{f.check}</span>
                              <Badge variant="outline" className={`text-[9px] ${severityTone(f.severity)}`}>
                                {f.severity}
                              </Badge>
                              {f.control && (
                                <Badge variant="outline" className="text-[9px]">
                                  {f.control}
                                </Badge>
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">
                              {f.provider?.toUpperCase()} · {f.detail}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default CloudPostureWidget;
